import { lazy, Suspense, useEffect, useState, type CSSProperties } from "react";
import { useLantern, useSession } from "../hooks";
import { moved, navigate, parse, useRoute } from "../router";
import { step } from "../switcher";
import { looksUnread, waitingCount } from "../sync/store";
import { Browse } from "./Browse";
import { ChannelView } from "./ChannelView";
import { SearchPanel } from "./SearchPanel";
import { Settings } from "./Settings";
import { Sidebar, sidebarOrder } from "./Sidebar";
import { QuickSwitcher } from "./QuickSwitcher";
import { ShortcutsHelp } from "./Shortcuts";
import { Resizer, sidebarEdge, useWidth } from "./Resizer";
import { Toasts } from "./Toasts";
import { ArtifactView } from "./ArtifactView";
import { LicenseBanner } from "./LicenseBanner";
import { PasswordRequestsBanner } from "./PasswordRequestsBanner";
import { Modal } from "./Modal";
import { InboxView } from "./InboxView";
import { ThreadsView } from "./ThreadsView";
import { SavedView } from "./SavedView";
import { TopBar } from "./TopBar";
import { ComposeView } from "./ComposeView";
import { ScheduledView } from "./ScheduledView";
import { EmojiView } from "./EmojiView";
import { HelpView } from "./HelpView";
import { FilesView } from "./FilesView";
import { HuddlesView } from "./HuddlesView";
import { UnreadsView } from "./UnreadsView";
import { PeopleView } from "./PeopleView";
import { ProfileHost } from "./ProfileCard";
import { ImageHost } from "./ImageViewer";
import { WorkflowsView } from "./WorkflowsView";
import { TasksView } from "./TasksView";
import { ThreadPanel } from "./ThreadPanel";
import { t, tn } from "../i18n";
import { CallWindow, IncomingCall } from "./Huddle";
import { DraftsView } from "./DraftsView";
import { backToChat, rememberChat, SettingsSidebar } from "./SettingsNav";
import { EmptyState, Page } from "./ui";
import { Reauth } from "./Reauth";

// Pages for admins and agents' owners load when first opened, so everyone
// else's first load does not carry them.
const WorkspaceSettings = lazy(() => import("./WorkspaceSettings").then((m) => ({ default: m.WorkspaceSettings })));
const FleetView = lazy(() => import("./FleetView").then((m) => ({ default: m.FleetView })));
const DiscoveryView = lazy(() => import("./DiscoveryView").then((m) => ({ default: m.DiscoveryView })));
const HealthView = lazy(() => import("./HealthView").then((m) => ({ default: m.HealthView })));
const HostedView = lazy(() => import("./HostedView").then((m) => ({ default: m.HostedView })));
const CapabilitiesView = lazy(() => import("./CapabilitiesView").then((m) => ({ default: m.CapabilitiesView })));
const ModelsView = lazy(() => import("./ModelsView").then((m) => ({ default: m.ModelsView })));
const TracesView = lazy(() => import("./TracesView").then((m) => ({ default: m.TracesView })));

// MenuButton opens the sidebar on a phone, where it is hidden; its badge
// counts what is waiting there: DMs and mentions.
function MenuButton({ onClick }: { onClick: () => void }) {
  const state = useLantern();
  let waiting = 0;
  for (const c of state.channels.values()) waiting += waitingCount(c.channel);
  return (
    <button className="menu-button" onClick={onClick} aria-label={t("Open the menu")}>
      <svg viewBox="0 0 20 20" width="20" height="20" aria-hidden>
        <path d="M3 5h14M3 10h14M3 15h14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
      {waiting > 0 && <span className="badge">{waiting > 99 ? "99+" : waiting}</span>}
    </button>
  );
}

export function Workspace({ onSignOut }: { onSignOut: () => void }) {
  const state = useLantern();
  const route = useRoute();
  const [thread, setThread] = useState<string | null>(null);
  const [browsing, setBrowsing] = useState(false);
  // On a phone the sidebar is a drawer; going anywhere closes it.
  const [drawer, setDrawer] = useState(false);
  useEffect(() => setDrawer(false), [route]);
  const [sidebarWidth, setSidebarWidth] = useWidth(sidebarEdge);
  const [switcher, setSwitcher] = useState(false);
  const [help, setHelp] = useState(false);
  const session = useSession();
  // One's settings, or the workspace's administration, take the sidebar's
  // place while open.
  const mode = route.settings !== undefined ? "settings" : route.admin !== undefined ? "admin" : null;

  // Keys that work anywhere: the switcher, the shortcuts, and moving
  // through the sidebar's channels.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const mod = e.metaKey || e.ctrlKey;
      if (mod && !e.altKey && !e.shiftKey && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setHelp(false);
        setSwitcher((on) => !on);
        return;
      }
      if (mod && !e.altKey && !e.shiftKey && e.key.toLowerCase() === "g") {
        e.preventDefault();
        document.querySelector<HTMLButtonElement>(".topbar-search")?.click();
        return;
      }
      if (e.key === "Escape" && e.shiftKey && !mod && !document.querySelector('[aria-modal="true"]')) {
        const unread = [...session.store.getSnapshot().channels.values()].filter((cs) => looksUnread(cs.channel) || waitingCount(cs.channel) > 0);
        if (unread.length && window.confirm(tn(unread.length, "Mark {n} channel as read?", "Mark all {n} channels as read?"))) {
          unread.forEach((cs) => void session.engine.markRead(cs.channel.id));
        }
        return;
      }
      // Escape leaves settings, when nothing open on the page takes it.
      if (e.key === "Escape" && !e.shiftKey && !mod && !e.defaultPrevented) {
        const r = parse(location.pathname);
        const field = e.target instanceof HTMLElement && (e.target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(e.target.tagName));
        if ((r.settings !== undefined || r.admin !== undefined) && !field && !document.querySelector('[aria-modal="true"], [role="menu"], [role="listbox"]')) {
          backToChat();
          return;
        }
      }
      if (mod && !e.altKey && e.key === "/") {
        e.preventDefault();
        setSwitcher(false);
        setHelp((on) => !on);
        return;
      }
      if (e.altKey && !mod && (e.key === "ArrowUp" || e.key === "ArrowDown")) {
        if (document.querySelector('[aria-modal="true"]')) return;
        const st = session.store.getSnapshot();
        const ids = sidebarOrder(session, st, parse(location.pathname).channel);
        const unread = (id: string) => {
          const c = st.channels.get(id)?.channel;
          return !!c && (looksUnread(c) || waitingCount(c) > 0);
        };
        const to = step(ids, parse(location.pathname).channel, e.key === "ArrowUp" ? -1 : 1, e.shiftKey ? unread : undefined);
        e.preventDefault();
        if (to) navigate(`/c/${to}`);
      }
    };
    addEventListener("keydown", onKey);
    return () => removeEventListener("keydown", onKey);
  }, [session]);

  // Open the first channel when none is chosen, unless the address is a page
  // that moved, which the next effect opens.
  useEffect(() => {
    if (Object.keys(route).length === 0 && state.channels.size > 0 && !moved(location.pathname, location.hash)) {
      navigate(`/c/${[...state.channels.keys()][0]}`);
    }
  }, [route, state.channels]);

  // Pages that moved into Settings or Administration: an old link still
  // opens them.
  useEffect(() => {
    const to = moved(location.pathname, location.hash);
    if (to) {
      history.replaceState(null, "", to);
      dispatchEvent(new PopStateEvent("popstate"));
    }
  }, [route]);

  // Back to chat returns to where one was before settings. The address is
  // read again, as the effect above may have just sent it to a settings page.
  useEffect(() => {
    const here = parse(location.pathname);
    if (here.settings === undefined && here.admin === undefined && !moved(location.pathname, location.hash)) rememberChat(location.pathname + location.search);
  }, [route]);

  // A permalink to a reply opens its thread.
  const target = route.channel && route.message ? state.channels.get(route.channel)?.messages.get(route.message) : undefined;
  useEffect(() => {
    if (target?.thread_root_id) setThread(target.thread_root_id);
  }, [target?.thread_root_id]);
  useEffect(() => setThread(null), [route.channel]);
  // A link to a thread opens it.
  useEffect(() => {
    if (route.thread) setThread(route.thread);
  }, [route.thread, route.channel]);

  return (
    <div className={`workspace ${drawer ? "drawer-open" : ""}`} style={{ "--sidebar-width": `${sidebarWidth}px` } as CSSProperties}>
      <TopBar path={location.pathname + location.search} onSearch={() => setSwitcher(true)} />
      <Reauth />
      {mode ? (
        <SettingsSidebar admin={mode === "admin"} page={route.settings ?? route.admin ?? ""} onSignOut={onSignOut} />
      ) : (
        <Sidebar
          current={route.channel}
          onJump={() => {
            setDrawer(false);
            setSwitcher(true);
          }}
          onBrowse={() => {
            setDrawer(false);
            setBrowsing(true);
          }}
          onSignOut={onSignOut}
        />
      )}
      <div className="drawer-backdrop" onClick={() => setDrawer(false)} aria-hidden />
      <Resizer edge={sidebarEdge} width={sidebarWidth} onResize={setSidebarWidth} label={t("Resize the sidebar")} style={{ left: sidebarWidth - 3 }} />
      <main>
        <MenuButton onClick={() => setDrawer(true)} />
        <IncomingCall />
        {state.connection === "offline" && <p className="banner">{t("Reconnecting…")}</p>}
        <LicenseBanner />
        <PasswordRequestsBanner />
        <Modal />
        <Toasts />
        <CallWindow />
        {state.connection === "signed_out" && (
          <p className="banner">
            {t("Your session ended.")} <button onClick={onSignOut}>{t("Sign in again")}</button>
          </p>
        )}
        <Suspense fallback={<p className="loading">{t("Loading…")}</p>}>
          {route.settings !== undefined ? (
            <Settings page={route.settings} onSignOut={onSignOut} />
          ) : route.admin !== undefined ? (
            state.me?.role !== "admin" ? (
              <Page title={t("Administration")}>
                <EmptyState icon="🔒" title={t("Only admins can open this page")} />
              </Page>
            ) : route.admin === "discovery" ? (
              <DiscoveryView />
            ) : route.admin === "hosted" ? (
              <HostedView />
            ) : route.admin === "capabilities" ? (
              <CapabilitiesView />
            ) : route.admin === "models" ? (
              <ModelsView />
            ) : route.admin === "system" ? (
              <HealthView />
            ) : (
              <WorkspaceSettings page={route.admin} />
            )
          ) : route.tasks ? (
            <TasksView />
          ) : route.fleet ? (
            <FleetView />
          ) : route.workflows ? (
            <WorkflowsView />
          ) : route.people ? (
            <PeopleView />
          ) : route.saved ? (
            <SavedView />
          ) : route.scheduled ? (
            <ScheduledView />
          ) : route.drafts ? (
            <DraftsView />
          ) : route.emoji ? (
            <EmojiView />
          ) : route.help ? (
            <HelpView />
          ) : route.compose ? (
            <ComposeView />
          ) : route.threads ? (
            <ThreadsView />
          ) : route.huddles ? (
            <HuddlesView />
          ) : route.files ? (
            <FilesView />
          ) : route.inbox ? (
            <InboxView />
          ) : route.unreads ? (
            <UnreadsView />
          ) : route.artifact ? (
            <ArtifactView id={route.artifact} />
          ) : route.traceAgent || route.trace ? (
            <TracesView agent={route.traceAgent} trace={route.trace} />
          ) : route.search ? (
            <SearchPanel />
          ) : route.channel ? (
            <ChannelView id={route.channel} highlight={route.message} onThread={setThread} />
          ) : (
            <section className="channel-view empty">{t("Join or create a channel to start.")}</section>
          )}
        </Suspense>
      </main>
      {thread && route.channel && <ThreadPanel channel={route.channel} root={thread} onClose={() => setThread(null)} />}
      {browsing && <Browse onClose={() => setBrowsing(false)} />}
      <ProfileHost />
      <ImageHost />
      {switcher && <QuickSwitcher onClose={() => setSwitcher(false)} />}
      {help && <ShortcutsHelp onClose={() => setHelp(false)} />}
    </div>
  );
}
